import {
  CartTargetingExpressionPayload,
  CartTargetingFe,
  CartTargetingFeDisplayDataType,
} from "@intelligems/ig-types";
import { expressionListToPostFix } from "@intelligems/ig-utils";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";
import { fromGqlId } from "@src/api/formatters/shopify";

import { CartTargetingState } from "./types";

const initialState: CartTargetingState = {
  cartTargeting: [],
  storedCartTargeting: [],
  isSuccess: false,
};

const cartTargetingSlice = rtk.createSlice({
  name: "cartTargeting",
  initialState,
  reducers: {
    resetCartTargeting: () => {
      return initialState;
    },
    initCartTargetingFromVariations: (
      draft,
      action: PayloadAction<{
        variations: any[];
      }>
    ) => {
      const { variations } = action.payload;
      const allCartTargeting: CartTargetingFe[] = [];

      variations.forEach((variation) => {
        variation.checkoutBlocks?.forEach((block: any) => {
          block.upsellProductRules?.forEach((rule: any) => {
            if (rule.cartTargeting) {
              allCartTargeting.push(rule.cartTargeting);
            }
          });
          if (block.displayConditions) {
            allCartTargeting.push(block.displayConditions);
          }
        });
      });

      if (allCartTargeting.length > 0) {
        draft.isSuccess = true;
        draft.cartTargeting = allCartTargeting;
        draft.storedCartTargeting = allCartTargeting;
      }
    },
    addCartTargeting: (
      draft,
      action: PayloadAction<{
        cartTargeting: CartTargetingFe;
      }>
    ) => {
      const { cartTargeting } = action.payload;
      draft.cartTargeting.push(cartTargeting);
    },
    replaceCartTargeting: (
      draft,
      action: PayloadAction<{
        cartTargetingIdsToRemove: string[];
        cartTargeting: CartTargetingFe[];
      }>
    ) => {
      const { cartTargetingIdsToRemove, cartTargeting } = action.payload;
      const idsToRemove = new Set(cartTargetingIdsToRemove);

      draft.cartTargeting = draft.cartTargeting.filter(
        (ct) => !idsToRemove.has(ct.id)
      );

      if (cartTargeting.length > 0) {
        draft.isSuccess = true;
        draft.cartTargeting.push(...cartTargeting);
      }
    },
    updateCartTargeting: (
      draft,
      action: PayloadAction<{
        cartTargetingId: string;
        expressionsList: CartTargetingExpressionPayload[];
      }>
    ) => {
      const { cartTargetingId, expressionsList } = action.payload;

      const postFix = expressionListToPostFix(expressionsList, "cartTargeting");

      const cartTargeting = draft.cartTargeting.find(
        (ct) => ct.id === cartTargetingId
      );
      if (cartTargeting && postFix) {
        cartTargeting.expression = postFix as typeof cartTargeting.expression;
      }
    },
    removeCartTargetingValue: (
      draft,
      action: PayloadAction<{
        valueToRemove: string;
        expressionIndex: number;
        cartTargetingId: string;
      }>
    ) => {
      const { valueToRemove, expressionIndex, cartTargetingId } =
        action.payload;

      const cartTargeting = draft.cartTargeting.find(
        (ct) => ct.id === cartTargetingId
      );
      if (!cartTargeting?.expression) return;

      const expression = cartTargeting.expression[expressionIndex];
      if (!expression?.query) return;

      const currentValue = expression.query.value as string;
      const splitValues = currentValue.split(",");
      const formattedValue =
        expression.query.type === "productId"
          ? fromGqlId(valueToRemove)
          : valueToRemove;

      expression.query.value = splitValues
        .filter((p) => p !== formattedValue)
        .join(",");

      const query = expression.query as {
        displayData?: Record<string, CartTargetingFeDisplayDataType>;
      };
      if (query.displayData && query.displayData[formattedValue]) {
        delete query.displayData[formattedValue];
      }
    },
    updateCartTargetingValue: (
      draft,
      action: PayloadAction<{
        value: number | string;
        expressionIndex: number;
        cartTargetingId: string;
        displayData?: Record<string, CartTargetingFeDisplayDataType>;
      }>
    ) => {
      const { value, expressionIndex, cartTargetingId, displayData } =
        action.payload;

      const cartTargeting = draft.cartTargeting.find(
        (ct) => ct.id === cartTargetingId
      );
      if (!cartTargeting?.expression) return;

      const expression = cartTargeting.expression[expressionIndex];
      if (!expression?.query) return;

      expression.query.value = value;
      if (displayData) {
        (
          expression.query as {
            displayData?: Record<string, CartTargetingFeDisplayDataType>;
          }
        ).displayData = displayData;
      }
    },
    updateCartTargetingCollectionTagValue: (
      draft,
      action: PayloadAction<{
        selectedValues: { label: string; value: string }[];
        expressionIndex: number;
        cartTargetingId: string;
      }>
    ) => {
      const { selectedValues, expressionIndex, cartTargetingId } =
        action.payload;

      const cartTargeting = draft.cartTargeting.find(
        (ct) => ct.id === cartTargetingId
      );
      if (!cartTargeting?.expression) return;

      const expression = cartTargeting.expression[expressionIndex];
      if (!expression?.query) return;

      const displayData: Record<string, CartTargetingFeDisplayDataType> = {};
      selectedValues.forEach((item) => {
        displayData[item.value] = {
          title: item.label,
        };
      });
      const formattedValue: string = selectedValues
        .map((item) => item.value)
        .join(",");

      expression.query = {
        ...expression.query,
        type: expression.query.type,
        key: expression.query.key,
        filter: expression.query.filter,
        value: formattedValue,
        displayData: displayData,
      };
    },
    processStoredCartTargeting: (draft) => {
      draft.storedCartTargeting = draft.cartTargeting;
    },
  },
  selectors: {
    getCartTargeting: (state) => state.cartTargeting,
    getStoredCartTargeting: (state) => state.storedCartTargeting,
    getCartTargetingById:
      (state) =>
      (id: string): CartTargetingFe | undefined =>
        state.cartTargeting.find((ct) => ct.id === id),
    getCartTargetingExpression: (state) => (id: string) =>
      state.cartTargeting.find((ct) => ct.id === id)?.expression ?? [],
  },
});

export const {
  initCartTargetingFromVariations,
  addCartTargeting,
  replaceCartTargeting,
  updateCartTargeting,
  removeCartTargetingValue,
  updateCartTargetingValue,
  updateCartTargetingCollectionTagValue,
  resetCartTargeting,
  processStoredCartTargeting,
} = cartTargetingSlice.actions;

export const cartTargetingReducer = cartTargetingSlice.reducer;
export const { getCartTargeting } = cartTargetingSlice.selectors;
